import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { useSection, resolveUrl } from "../lib/useCMS";

const FALLBACK = {
  overline: "About",
  heading_top: "Built by a pro.",
  heading_bottom: "Taught by a teacher.",
  bio: "Coach Mike is the founder of Built Different Hockey, a defensemen-specific development program built on structure, discipline, details and long-term player growth. His mission is simple: develop defensemen with strong foundations, calm confidence, and a relentless pursuit of excellence on and off the ice.",
  image_url: "https://images.pexels.com/photos/6847302/pexels-photo-6847302.jpeg?auto=compress&cs=tinysrgb&w=1400",
  photo_overline: "Founder · Head Coach",
  name_first: "Coach",
  name_accent: "Mike",
  name_last: "Fournier",
  credentials_label: "The Credentials",
  credentials: [
    "Former Professional Defenseman",
    "Certified Personal Trainer",
    "Certified Nutrition Coach",
    "Masters Degree",
    "USA Hockey Certified Coach",
    "Published Hockey Development Author",
    "20 Year Military Veteran",
  ],
};

export const Coach = () => {
  const c = useSection("coach", FALLBACK);
  const credentials = Array.isArray(c.credentials) && c.credentials.length ? c.credentials : FALLBACK.credentials;

  return (
    <section id="coach" data-testid="coach-section" className="relative bg-[#0f0f0f] py-24 md:py-32 border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-12 gap-x-0 gap-y-6 md:gap-10">
          <div className="col-span-12 lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden bento-card">
              <img src={resolveUrl(c.image_url)} alt="Coach Mike Fournier, founder of Built Different Hockey" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover object-center grayscale contrast-125" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
              <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/80 font-bold">{c.photo_overline}</div>
                <div className="h-2 w-2 bg-brand-light" />
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="font-display text-5xl md:text-6xl uppercase leading-[0.9]">
                  {c.name_first}
                  <br />
                  <span className="text-brand-light">{c.name_accent}</span>
                  <br />
                  {c.name_last}
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-7 flex flex-col justify-center">
            <p className="overline">{c.overline}</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase mt-5 leading-[0.95]" data-testid="coach-heading">
              {c.heading_top}
              <br />
              <span className="text-brand-light">{c.heading_bottom}</span>
            </h2>
            <p className="mt-6 text-zinc-300 leading-relaxed max-w-2xl whitespace-pre-line">{c.bio}</p>

            <div className="mt-10">
              <div className="text-[10px] tracking-[0.3em] uppercase text-brand-light font-bold mb-5">{c.credentials_label}</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="coach-credentials">
                {credentials.map((cr, i) => (
                  <motion.li key={`${cr}-${i}`} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.07 }} className="flex items-center gap-3 border-b border-white/10 pb-3">
                    <CheckCircle2 className="h-4 w-4 text-brand-light shrink-0" />
                    <span className="text-sm md:text-base text-zinc-200">{cr}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
